/**
 * @module reducers/AudioPlayer
 */

import { Map, Set } from 'immutable';
import isNil from 'lodash/isNil';

import * as AudioConstants from './constants';
import * as D from './deps';
import { check } from './libs/BandwidthMonitor';
import * as AudioUtils from './utils';

const EMPTY_STRING = '';
// For these error types we will only increment the count
// if the error occurs within ERROR_RATE_LIMIT_MS timeframe
const RATE_LIMITED_ERROR_TYPES = Set([
  AudioConstants.CommonAudioErrorCodes.SRC_NOT_SUPPORTED,
  AudioConstants.CommonAudioErrorCodes.NETWORK,
]);

/**
 * The initial state.
 */
export const INITIAL_STATE = Map({
  [AudioConstants.AudioPlayerKeys.INIT]: false,
  [AudioConstants.AudioPlayerKeys.PLAYING]: false,
  [AudioConstants.AudioPlayerKeys.EXPLICITLY_PAUSED]: false,
  [AudioConstants.AudioPlayerKeys.CURRENT_TIME]: 0,
  [AudioConstants.AudioPlayerKeys.CURRENT_TIMESTAMP]: D.Utils.Time.getNow(),
  [AudioConstants.AudioPlayerKeys.DURATION]: 0,
  [AudioConstants.AudioPlayerKeys.LOADING]: false,
  [AudioConstants.AudioPlayerKeys.BUFFERING]: false,
  [AudioConstants.AudioPlayerKeys.BUFFERED]: 0,
  [AudioConstants.AudioPlayerKeys.BUFFERED_REMAINING]: null,
  [AudioConstants.AudioPlayerKeys.ERROR]: false,
  [AudioConstants.AudioPlayerKeys.LAST_ERROR_TIMESTAMP]: null,
  [AudioConstants.AudioPlayerKeys.BANDWIDTH_DIFFERENCE]: 0,
  [AudioConstants.AudioPlayerKeys.HAS_EXPERIENCED_BANDWIDTH_ERROR]: false,
  [AudioConstants.AudioPlayerKeys.ERROR_OBJ]: Map({
    [AudioConstants.AudioPlayerKeys.ERROR_MSG]: EMPTY_STRING,
    [AudioConstants.AudioPlayerKeys.ERROR_TYPE]: -1,
  }),
  [AudioConstants.AudioPlayerKeys.ERROR_COUNTS]: Map({
    // Audio Harness Errors
    [AudioConstants.CommonAudioErrorCodes.UNKNOWN]: 0,
    [AudioConstants.CommonAudioErrorCodes.ABORTED]: 0,
    [AudioConstants.CommonAudioErrorCodes.NETWORK]: 0,
    [AudioConstants.CommonAudioErrorCodes.DECODE]: 0,
    [AudioConstants.CommonAudioErrorCodes.SRC_NOT_SUPPORTED]: 0,
    [AudioConstants.CommonAudioErrorCodes.INIT_TIMEOUT]: 0,
    // Errors generated from app
    [AudioConstants.AudioPlayerKeys.BANDWIDTH_ERRORS]: 0,
  }),
  [AudioConstants.AudioPlayerKeys.TRACK_TOKEN]: null,
  [AudioConstants.AudioPlayerKeys.ACTIVE_TRACK_URL]: null,
  [AudioConstants.AudioPlayerKeys.HAS_WAKE_LOCK]: false,
  [AudioConstants.AudioPlayerKeys.AUX_PLAYBACK_PLAYING]: false,
  [AudioConstants.AudioPlayerKeys.AUX_PLAYBACK_URL]: null,
  [AudioConstants.AudioPlayerKeys.INIT_AUDIO_RETRIES]: 0,
});

// AUDIOPLAYER REDUCER
export default D.createReducer(INITIAL_STATE, {
  [D.Constants.AudioPlayer.AudioPlayerActions.AUDIO_INITIALIZED]: onInit,
  [D.Constants.AudioPlayer.AudioPlayerActions.AUDIO_PLAY_TRACK]: audioPlayTrack,
  [D.Constants.AudioPlayer.AudioPlayerActions.RESET]: reset,
  [D.Constants.AudioPlayer.AudioPlayerActions.LOAD]: load,
  [D.Constants.AudioPlayer.AudioPlayerActions.PROGRESS]: progress,
  [D.Constants.AudioPlayer.AudioPlayerActions.PLAYING]: playing,
  [D.Constants.AudioPlayer.AudioPlayerActions.PAUSE]: pause,
  [D.Constants.AudioPlayer.AudioPlayerActions.PAUSED]: paused,
  [D.Constants.AudioPlayer.AudioPlayerActions.DURATION]: audioDuration,
  [D.Constants.AudioPlayer.AudioPlayerActions.ENDED]: ended,
  [D.Constants.AudioPlayer.AudioPlayerActions.UPDATE]: update,
  [D.Constants.AudioPlayer.AudioPlayerActions.ERROR]: audioError,
  [D.Constants.AudioPlayer.AudioPlayerActions.SET_ACTIVE_TRACK_URL]: setActiveTrackUrl,
  [D.Constants.AudioPlayer.AudioPlayerActions.SET_WAKE_LOCK]: setWakeLock,
  [D.Constants.Samples.SamplePlayerActions.PLAYING]: auxPlaying,
  [D.Constants.Samples.SamplePlayerActions.PAUSED]: auxPaused,
  [D.Constants.Samples.SamplePlayerActions.STOPPED]: auxStopped,
  [D.Constants.Playback.ActionTypes.RELOAD_AUDIOPLAYBACK]: retryInit,
  [D.Constants.Playback.ActionTypes.RESET_PLAYLIST_INDEX]: reset,
});

// CASE FUNCTIONS

function onInit(state) {
  return state.merge({
    INIT: true,
  });
}

function retryInit(state) {
  return state.update(AudioConstants.AudioPlayerKeys.INIT_AUDIO_RETRIES, retries => retries + 1);
}

function audioPlayTrack(state, { payload: { track, AUDIO_CURRENT_TIME: audioCurrentTime } }) {
  return state.merge({
    [AudioConstants.AudioPlayerKeys.TRACK_TOKEN]: D.Utils.Core.getFromMap(track, 'trackToken'),
    [AudioConstants.AudioPlayerKeys.CURRENT_TIME]: isNil(audioCurrentTime)
      ? state.get(AudioConstants.AudioPlayerKeys.CURRENT_TIME)
      : audioCurrentTime,
  });
}

// the audio player needs to reset
function reset(state) {
  return INITIAL_STATE.set(AudioConstants.AudioPlayerKeys.DURATION, 0);
}

// the audio data has begun loading, this implies we will play audio
// but we either don't have the meta data or haven't buffered enough
function load(state) {
  return state.merge({
    [AudioConstants.AudioPlayerKeys.LOADING]: true,
    [AudioConstants.AudioPlayerKeys.BUFFERING]: true,
    [AudioConstants.AudioPlayerKeys.BUFFERED]: 0,
  });
}

// buffering progress update
function progress(state, { payload }) {
  return state.merge({
    [AudioConstants.AudioPlayerKeys.BUFFERING]: true,
    [AudioConstants.AudioPlayerKeys.BUFFERED]: payload.AUDIO_BUFFERED,
  });
}

// the audio player is actively playing the track
function playing(state) {
  return state.merge({
    [AudioConstants.AudioPlayerKeys.PLAYING]: true,
    [AudioConstants.AudioPlayerKeys.EXPLICITLY_PAUSED]: false,
  });
}

function pause(state, { payload: { isExplicit } }) {
  if (isExplicit) {
    return state.set(AudioConstants.AudioPlayerKeys.EXPLICITLY_PAUSED, true);
  }
  return state;
}

// the audio player has been paused
function paused(state) {
  return state.set(AudioConstants.AudioPlayerKeys.PLAYING, false);
}

// enough data is loaded to know the duration value
function audioDuration(state, { payload }) {
  return state.set(AudioConstants.AudioPlayerKeys.DURATION, payload.AUDIO_DURATION);
}

function ended() {
  return INITIAL_STATE;
}

// there's been an update to the current time of the audio
function update(state, { payload }) {
  const existingToken = state.get(AudioConstants.AudioPlayerKeys.TRACK_TOKEN);
  const existingUrl = state.get(AudioConstants.AudioPlayerKeys.ACTIVE_TRACK_URL);

  const currentToken = payload.token;
  const currentUrl = payload.url;

  if (
    (existingToken && existingToken !== currentToken) ||
    (existingUrl && existingUrl !== currentUrl)
  ) {
    return state;
  }

  const currentPlaytime = payload.AUDIO_CURRENT_TIME;

  let bufferedAmount = 0;
  const duration = payload.AUDIO_DURATION || 0;
  let percentLoaded = 0;
  let secondsOfAudioLeft = 0;

  if (payload.AUDIO_CURRENT_BUFFER_END) {
    bufferedAmount = AudioUtils.getBuffered(payload.AUDIO_CURRENT_BUFFER_END);
    percentLoaded = Math.min((bufferedAmount / duration) * 100, 100);
    secondsOfAudioLeft =
      bufferedAmount > currentPlaytime ? bufferedAmount - currentPlaytime : bufferedAmount;
  }

  const bandwidthCheck = check(
    currentPlaytime,
    percentLoaded,
    secondsOfAudioLeft,
    state.get(AudioConstants.AudioPlayerKeys.BANDWIDTH_DIFFERENCE),
    state.getIn([
      AudioConstants.AudioPlayerKeys.ERROR_COUNTS,
      AudioConstants.AudioPlayerKeys.BANDWIDTH_ERRORS,
    ]),
  );

  return state
    .merge({
      [AudioConstants.AudioPlayerKeys.CURRENT_TIME]: currentPlaytime,
      [AudioConstants.AudioPlayerKeys.CURRENT_TIMESTAMP]: D.Utils.Time.getNow(),
      [AudioConstants.AudioPlayerKeys.DURATION]: duration,
      [AudioConstants.AudioPlayerKeys.BUFFERED]: bufferedAmount,
      [AudioConstants.AudioPlayerKeys.BUFFERED_REMAINING]: payload.AUDIO_BUFFERED_REMAINING || null,
      [AudioConstants.AudioPlayerKeys.BANDWIDTH_DIFFERENCE]: bandwidthCheck.lastDifference,
      [AudioConstants.AudioPlayerKeys.HAS_EXPERIENCED_BANDWIDTH_ERROR]:
        state.get(AudioConstants.AudioPlayerKeys.HAS_EXPERIENCED_BANDWIDTH_ERROR) ||
        bandwidthCheck.warningCount > AudioConstants.MAX_BANDWIDTH_WARNINGS,
    })
    .setIn(
      [
        AudioConstants.AudioPlayerKeys.ERROR_COUNTS,
        AudioConstants.AudioPlayerKeys.BANDWIDTH_ERRORS,
      ],
      bandwidthCheck.warningCount,
    );
}

// for errors reported from the player and passed on via middleware
function audioError(state, { payload: { ERROR_OBJ: error } }) {
  const errorType = error.commonType;
  const errorMsg = (error && error.message) || '';
  const lastErrorTime = state.get(AudioConstants.AudioPlayerKeys.LAST_ERROR_TIMESTAMP);
  const currentErrorTimeStamp = D.Utils.Time.getNow();
  let stateToMerge = state;

  if (
    RATE_LIMITED_ERROR_TYPES.has(errorType) &&
    currentErrorTimeStamp &&
    currentErrorTimeStamp - lastErrorTime > AudioConstants.ERROR_RATE_LIMIT_MS
  ) {
    // we reset error counts for designated errors if they happen across enough time.
    // For example, if a user sees 5 expired urls in a 24 hours session we aren't concerned;
    // however if they see 5 in 1 second that's an issue.
    stateToMerge = state.setIn([AudioConstants.AudioPlayerKeys.ERROR_COUNTS, errorType], 1);
  } else if (state.getIn([AudioConstants.AudioPlayerKeys.ERROR_COUNTS, errorType], -1) >= 0) {
    // We're updating error counts defensively, then updating the current UI error and message
    stateToMerge = state.updateIn(
      [AudioConstants.AudioPlayerKeys.ERROR_COUNTS, errorType],
      value => value + 1,
    );
  }

  if (
    [
      AudioConstants.CommonAudioErrorCodes.BROWSER_AUTOPLAY_BLOCKED,
      AudioConstants.CommonAudioErrorCodes.SERVICE_ERROR,
    ].includes(errorType)
  ) {
    // If browser blocks autoplay, we want to consider that an explicit pause to reflect correct UI
    stateToMerge = stateToMerge.set(AudioConstants.AudioPlayerKeys.EXPLICITLY_PAUSED, true);
  }

  return stateToMerge.mergeDeep({
    [AudioConstants.AudioPlayerKeys.ERROR]: true,
    [AudioConstants.AudioPlayerKeys.ACTIVE_TRACK_URL]: null,
    [AudioConstants.AudioPlayerKeys.LAST_ERROR_TIMESTAMP]: currentErrorTimeStamp,
    [AudioConstants.AudioPlayerKeys.ERROR_OBJ]: {
      [AudioConstants.AudioPlayerKeys.ERROR_MSG]: errorMsg,
      [AudioConstants.AudioPlayerKeys.ERROR_TYPE]: errorType,
    },
  });
}

function setActiveTrackUrl(state, { payload: { url } }) {
  return state.set(AudioConstants.AudioPlayerKeys.ACTIVE_TRACK_URL, url);
}

function setWakeLock(state, action) {
  return state.set(AudioConstants.AudioPlayerKeys.HAS_WAKE_LOCK, action.isOn);
}

function auxPaused(state) {
  return state.merge({
    [AudioConstants.AudioPlayerKeys.AUX_PLAYBACK_PLAYING]: false,
    [AudioConstants.AudioPlayerKeys.AUX_PLAYBACK_URL]: null,
  });
}

function auxStopped(state) {
  return state.merge({
    [AudioConstants.AudioPlayerKeys.AUX_PLAYBACK_PLAYING]: false,
    [AudioConstants.AudioPlayerKeys.AUX_PLAYBACK_URL]: null,
  });
}

function auxPlaying(state, { payload }) {
  return state.merge({
    [AudioConstants.AudioPlayerKeys.AUX_PLAYBACK_PLAYING]: true,
    [AudioConstants.AudioPlayerKeys.AUX_PLAYBACK_URL]: payload.url,
  });
}
